import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Building2, Download, KeyRound, Pencil, Power, ShieldAlert, ShieldCheck, ShoppingBag, UploadCloud, Users, WifiOff } from 'lucide-react';
import { api } from '../../lib/api';
import type { BridgeAppRelease } from '../../types';

type PlatformOverview = {
  restaurants: number;
  users: number;
  orders: number;
};

type TechnicalHealthIssue = {
  severity: 'critical' | 'warning' | 'info';
  code: string;
  message: string;
};

type TechnicalHealthRestaurant = {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  owner?: {
    id: string;
    name: string;
    email: string;
  } | null;
  healthScore: number;
  issueCounts: {
    critical: number;
    warning: number;
    info: number;
  };
  signals: {
    paymentConfigured: boolean;
    whatsappStatus: string;
    whatsappWebhookVerified: boolean;
    printingEnabled: boolean;
    activePrinterConfigs: number;
    activeBridgeDevices: number;
    onlineBridgeDevices: number;
    staleBridgeDevices: number;
    failedPrintJobs: number;
    pendingPrintJobs: number;
    atRiskMercadoPagoOrders: number;
    paidAwaitingOrders: number;
    has3dModelModule: boolean;
    productsWith3d: number;
    productsWithoutUsdz: number;
  };
  samples: {
    atRiskOrders: Array<{
      id: string;
      shortId: string;
      total: number;
      status: string;
      paymentStatus: string | null;
      createdAt: string;
    }>;
    printFailures: Array<{
      id: string;
      createdAt: string;
      printerName: string;
      orderShortId: string | null;
      errorMessage: string;
    }>;
  };
  issues: TechnicalHealthIssue[];
};

type TechnicalHealthPayload = {
  generatedAt: string;
  summary: {
    restaurants: number;
    restaurantsWithIssues: number;
    criticalRestaurants: number;
    warningRestaurants: number;
    paymentRiskOrders: number;
    paidAwaitingOrders: number;
    failedPrintJobs: number;
    staleBridgeDevices: number;
  };
  restaurants: TechnicalHealthRestaurant[];
};

type PlatformRestaurant = {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  subscriptionPlan: 'BASIC' | 'PRO' | 'ENTERPRISE';
  subscriptionStatus: 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'CANCELED';
  billingCycle: 'MONTHLY' | 'YEARLY';
  subscriptionPrice: number;
  trialEndsAt?: string | null;
  nextBillingAt?: string | null;
  address?: string;
  phone?: string;
  settings?: {
    hasReservationModule?: boolean;
    hasTableModule?: boolean;
    hasReservations?: boolean;
    has3dModelModule?: boolean;
    hasQrMenu?: boolean;
    printingModuleEnabled?: boolean;
    deliveryEnabled?: boolean;
    pickupEnabled?: boolean;
    deliveryFixedFee?: number;
    deliveryPaymentMethods?: string[];
    currency?: string;
    theme?: string;
  } | null;
  createdAt: string;
  owner?: {
    id: string;
    name: string;
    email: string;
  } | null;
  _count: {
    users: number;
    orders: number;
    categories: number;
    products: number;
  };
};

type PlatformUser = {
  id: string;
  name: string;
  email: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'STAFF';
  establishment?: {
    id: string;
    name: string;
    slug: string;
    active: boolean;
  } | null;
  createdAt: string;
};

type OperationalPreset = 'DELIVERY_ONLY' | 'DINING_WITH_RESERVATIONS' | 'DINING_WITHOUT_RESERVATIONS';
type PlatformSection = 'overview' | 'stability' | 'bridge' | 'restaurants' | 'users';

const deliveryPaymentMethodOptions = [
  {
    value: 'MERCADO_PAGO',
    label: 'Mercado Pago',
    description: 'Checkout online no delivery.',
  },
  {
    value: 'CARD_ON_DELIVERY',
    label: 'Cartao na entrega',
    description: 'Pagamento presencial na entrega.',
  },
  {
    value: 'CASH',
    label: 'Dinheiro',
    description: 'Recebimento manual no ato da entrega.',
  },
] as const;

const initialCreateForm = {
  name: '',
  slug: '',
  address: '',
  phone: '',
  trialDays: '14',
  operationalPreset: undefined as OperationalPreset | undefined,
  hasReservationModule: true,
  hasTableModule: true,
  has3dModelModule: false,
  hasQrMenu: true,
  printingModuleEnabled: true,
  deliveryEnabled: true,
  pickupEnabled: true,
  currency: 'BRL',
  theme: 'dark',
  deliveryFixedFee: '0',
  deliveryPaymentMethods: ['MERCADO_PAGO', 'CARD_ON_DELIVERY', 'CASH'],
  ownerName: '',
  ownerEmail: '',
  ownerPassword: '',
};

const sectionCopy: Record<PlatformSection, { title: string; description: string }> = {
  overview: {
    title: 'Visao geral',
    description: 'Resumo da plataforma com os principais indicadores da base.',
  },
  stability: {
    title: 'Estabilidade tecnica',
    description: 'Monitore riscos de pagamento, impressao, WhatsApp e ativos 3D por restaurante.',
  },
  bridge: {
    title: 'Bridge App Android',
    description: 'Gerencie releases do app de impressao distribuido para os restaurantes.',
  },
  restaurants: {
    title: 'Restaurantes',
    description: 'Crie tenants, ajuste modulos, assinaturas e o acesso do dono.',
  },
  users: {
    title: 'Usuarios da plataforma',
    description: 'Visao global dos acessos cadastrados na operacao.',
  },
};

const PlatformDashboard: React.FC<{ section: PlatformSection }> = ({ section }) => {
  const [overview, setOverview] = useState<PlatformOverview | null>(null);
  const [technicalHealth, setTechnicalHealth] = useState<TechnicalHealthPayload | null>(null);
  const [focusedHealthRestaurantId, setFocusedHealthRestaurantId] = useState<string | null>(null);
  const [restaurants, setRestaurants] = useState<PlatformRestaurant[]>([]);
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    slug: '',
    address: '',
    phone: '',
    hasReservationModule: true,
    hasTableModule: true,
    has3dModelModule: false,
    hasQrMenu: true,
    printingModuleEnabled: true,
    deliveryEnabled: true,
    pickupEnabled: true,
  });
  const [subscriptionDrafts, setSubscriptionDrafts] = useState<Record<string, {
    subscriptionPlan: PlatformRestaurant['subscriptionPlan'];
    subscriptionStatus: PlatformRestaurant['subscriptionStatus'];
    billingCycle: PlatformRestaurant['billingCycle'];
    subscriptionPrice: string;
    trialDays: string;
  }>>({});
  const [passwordDrafts, setPasswordDrafts] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savingRestaurantId, setSavingRestaurantId] = useState<string | null>(null);
  const [togglingRestaurantId, setTogglingRestaurantId] = useState<string | null>(null);
  const [resettingOwnerId, setResettingOwnerId] = useState<string | null>(null);
  const [savingSubscriptionRestaurantId, setSavingSubscriptionRestaurantId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bridgeReleases, setBridgeReleases] = useState<BridgeAppRelease[]>([]);
  const [bridgeReleaseForm, setBridgeReleaseForm] = useState({
    version: '',
    build: '1',
    releasedAt: '',
    minAndroid: '8.0',
    file: null as File | null,
  });
  const operationalPresets: Array<{
    id: OperationalPreset;
    label: string;
    description: string;
    values: {
      hasTableModule: boolean;
      hasReservationModule: boolean;
      deliveryEnabled: boolean;
      pickupEnabled: boolean;
    };
  }> = [
    {
      id: 'DELIVERY_ONLY',
      label: 'Somente delivery',
      description: 'Desativa mesas e reservas para operacoes sem salao proprio.',
      values: {
        hasTableModule: false,
        hasReservationModule: false,
        deliveryEnabled: true,
        pickupEnabled: true,
      },
    },
    {
      id: 'DINING_WITH_RESERVATIONS',
      label: 'Salao + reservas',
      description: 'Libera mesas e agenda completa de reservas.',
      values: {
        hasTableModule: true,
        hasReservationModule: true,
        deliveryEnabled: false,
        pickupEnabled: false,
      },
    },
    {
      id: 'DINING_WITHOUT_RESERVATIONS',
      label: 'Salao sem reservas',
      description: 'Libera mesas, mas deixa a agenda de reservas desligada.',
      values: {
        hasTableModule: true,
        hasReservationModule: false,
        deliveryEnabled: false,
        pickupEnabled: false,
      },
    },
  ];

  const loadData = async () => {
    try {
      const [overviewRes, technicalHealthRes, restaurantsRes, usersRes, bridgeReleasesRes] = await Promise.all([
        api.get('/platform/overview'),
        api.get('/platform/technical-health'),
        api.get('/platform/restaurants'),
        api.get('/platform/users'),
        api.get('/admin/bridge-app/android/releases'),
      ]);
      setOverview(overviewRes.data);
      setTechnicalHealth(technicalHealthRes.data);
      setFocusedHealthRestaurantId((current) => current ?? technicalHealthRes.data?.restaurants?.find((restaurant: TechnicalHealthRestaurant) => restaurant.issues.length > 0)?.id ?? null);
      setRestaurants(restaurantsRes.data);
      setUsers(usersRes.data);
      setBridgeReleases(bridgeReleasesRes.data ?? []);
      setSubscriptionDrafts(
        Object.fromEntries(
          restaurantsRes.data.map((restaurant: PlatformRestaurant) => [
            restaurant.id,
            {
              subscriptionPlan: restaurant.subscriptionPlan,
              subscriptionStatus: restaurant.subscriptionStatus,
              billingCycle: restaurant.billingCycle,
              subscriptionPrice: String(restaurant.subscriptionPrice ?? 0),
              trialDays: getRemainingTrialDays(restaurant.trialEndsAt),
            },
          ]),
        ),
      );
    } catch (requestError) {
      console.error('Failed to load platform data', requestError);
      setError('Nao foi possivel carregar os dados da plataforma.');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const metricCards = [
    { label: 'Restaurantes', value: overview?.restaurants ?? 0, icon: Building2 },
    { label: 'Usuarios', value: overview?.users ?? 0, icon: Users },
    { label: 'Pedidos', value: overview?.orders ?? 0, icon: ShoppingBag },
  ];

  const stabilityCards = useMemo(() => ([
    { label: 'Restaurantes com risco', value: technicalHealth?.summary.restaurantsWithIssues ?? 0, icon: ShieldAlert, tone: 'warning' },
    { label: 'Criticos', value: technicalHealth?.summary.criticalRestaurants ?? 0, icon: AlertTriangle, tone: 'critical' },
    { label: 'Pagamentos em risco', value: technicalHealth?.summary.paymentRiskOrders ?? 0, icon: ShieldAlert, tone: 'warning' },
    { label: 'Bridges offline', value: technicalHealth?.summary.staleBridgeDevices ?? 0, icon: WifiOff, tone: 'critical' },
  ]), [technicalHealth]);

  const restaurantsWithIssues = useMemo(
    () =>
      (technicalHealth?.restaurants ?? [])
        .filter((restaurant) => restaurant.issues.length > 0)
        .sort((left, right) => {
          if (right.issueCounts.critical !== left.issueCounts.critical) {
            return right.issueCounts.critical - left.issueCounts.critical;
          }
          if (right.issueCounts.warning !== left.issueCounts.warning) {
            return right.issueCounts.warning - left.issueCounts.warning;
          }
          return left.healthScore - right.healthScore;
        }),
    [technicalHealth],
  );

  const focusedHealthRestaurant = useMemo(() => {
    if (!restaurantsWithIssues.length) {
      return null;
    }

    return restaurantsWithIssues.find((restaurant) => restaurant.id === focusedHealthRestaurantId)
      ?? restaurantsWithIssues[0];
  }, [focusedHealthRestaurantId, restaurantsWithIssues]);

  const pageCopy = sectionCopy[section];

  const createSelectedPreset = operationalPresets.find(
    (preset) =>
      preset.values.hasTableModule === createForm.hasTableModule &&
      preset.values.hasReservationModule === createForm.hasReservationModule &&
      preset.values.deliveryEnabled === createForm.deliveryEnabled &&
      preset.values.pickupEnabled === createForm.pickupEnabled,
  )?.id;

  const editSelectedPreset = operationalPresets.find(
    (preset) =>
      preset.values.hasTableModule === editForm.hasTableModule &&
      preset.values.hasReservationModule === editForm.hasReservationModule &&
      preset.values.deliveryEnabled === editForm.deliveryEnabled &&
      preset.values.pickupEnabled === editForm.pickupEnabled,
  )?.id;

  const handleCreateChange = (field: keyof typeof initialCreateForm, value: string) => {
    setCreateForm((current) => ({ ...current, [field]: value }));
  };

  const handleCreateToggle = (
    field:
      | 'hasReservationModule'
      | 'hasTableModule'
      | 'has3dModelModule'
      | 'hasQrMenu'
      | 'printingModuleEnabled'
      | 'deliveryEnabled'
      | 'pickupEnabled',
    value: boolean,
  ) => {
    setCreateForm((current) => ({ ...current, operationalPreset: undefined, [field]: value }));
  };

  const handleCreatePaymentMethodToggle = (method: (typeof deliveryPaymentMethodOptions)[number]['value']) => {
    setCreateForm((current) => ({
      ...current,
      deliveryPaymentMethods: current.deliveryPaymentMethods.includes(method)
        ? current.deliveryPaymentMethods.filter((item) => item !== method)
        : [...current.deliveryPaymentMethods, method],
    }));
  };

  const applyCreatePreset = (presetId: OperationalPreset) => {
    const selectedPreset = operationalPresets.find((preset) => preset.id === presetId);
    if (!selectedPreset) return;

    setCreateForm((current) => ({
      ...current,
      operationalPreset: presetId,
      ...selectedPreset.values,
    }));
  };

  const applyEditPreset = (presetId: OperationalPreset) => {
    const selectedPreset = operationalPresets.find((preset) => preset.id === presetId);
    if (!selectedPreset) return;

    setEditForm((current) => ({
      ...current,
      ...selectedPreset.values,
    }));
  };

  const handleCreateRestaurant = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setIsSubmitting(true);
      setError(null);
      setFeedback(null);

      const { data } = await api.post('/platform/restaurants', {
        ...createForm,
        slug: createForm.slug.trim().toLowerCase(),
        trialDays: Number(createForm.trialDays),
        deliveryFixedFee: Math.max(0, Number(createForm.deliveryFixedFee) || 0),
      });

      setFeedback(`Restaurante criado. Dono inicial: ${data.credentials.email}`);
      setCreateForm(initialCreateForm);
      await loadData();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Nao foi possivel criar o restaurante.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditing = (restaurant: PlatformRestaurant) => {
    setEditingId(restaurant.id);
    setEditForm({
      name: restaurant.name,
      slug: restaurant.slug,
      address: restaurant.address ?? '',
      phone: restaurant.phone ?? '',
      hasReservationModule: restaurant.settings?.hasReservationModule !== false,
      hasTableModule: restaurant.settings?.hasTableModule !== false,
      has3dModelModule: restaurant.settings?.has3dModelModule === true,
      hasQrMenu: restaurant.settings?.hasQrMenu !== false,
      printingModuleEnabled: restaurant.settings?.printingModuleEnabled !== false,
      deliveryEnabled: restaurant.settings?.deliveryEnabled !== false,
      pickupEnabled: restaurant.settings?.pickupEnabled !== false,
    });
    setFeedback(null);
    setError(null);
  };

  const saveRestaurant = async (id: string) => {
    try {
      setSavingRestaurantId(id);
      setError(null);
      setFeedback(null);
      await api.patch(`/platform/restaurants/${id}`, {
        ...editForm,
        slug: editForm.slug.trim().toLowerCase(),
      });
      setEditingId(null);
      setFeedback('Restaurante atualizado com sucesso.');
      await loadData();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Nao foi possivel atualizar o restaurante.');
    } finally {
      setSavingRestaurantId(null);
    }
  };

  const toggleRestaurantStatus = async (restaurant: PlatformRestaurant) => {
    try {
      setTogglingRestaurantId(restaurant.id);
      setError(null);
      setFeedback(null);
      await api.patch(`/platform/restaurants/${restaurant.id}/status`, {
        active: !restaurant.active,
      });
      setFeedback(restaurant.active ? 'Restaurante desativado.' : 'Restaurante reativado.');
      await loadData();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Nao foi possivel alterar o status do restaurante.');
    } finally {
      setTogglingRestaurantId(null);
    }
  };

  const resetOwnerPassword = async (userId: string) => {
    const password = passwordDrafts[userId]?.trim();
    if (!password) {
      setError('Defina uma nova senha antes de resetar.');
      return;
    }

    try {
      setResettingOwnerId(userId);
      setError(null);
      setFeedback(null);
      const { data } = await api.patch(`/platform/users/${userId}/reset-password`, {
        password,
      });
      setFeedback(`Senha redefinida para ${data.email}.`);
      setPasswordDrafts((current) => ({ ...current, [userId]: '' }));
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Nao foi possivel redefinir a senha.');
    } finally {
      setResettingOwnerId(null);
    }
  };

  const saveSubscription = async (restaurantId: string) => {
    const draft = subscriptionDrafts[restaurantId];
    if (!draft) return;

    try {
      setSavingSubscriptionRestaurantId(restaurantId);
      setError(null);
      setFeedback(null);
      await api.patch(`/platform/restaurants/${restaurantId}/subscription`, {
        subscriptionPlan: draft.subscriptionPlan,
        subscriptionStatus: draft.subscriptionStatus,
        billingCycle: draft.billingCycle,
        subscriptionPrice: Number(draft.subscriptionPrice),
        trialDays: draft.trialDays === '' ? null : Number(draft.trialDays),
      });
      setFeedback('Assinatura atualizada com sucesso.');
      await loadData();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Nao foi possivel atualizar a assinatura.');
    } finally {
      setSavingSubscriptionRestaurantId(null);
    }
  };

  const handleBridgeReleaseUpload = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!bridgeReleaseForm.file) {
      setError('Selecione o arquivo APK antes de enviar.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setFeedback(null);
      const formData = new FormData();
      formData.append('file', bridgeReleaseForm.file);
      formData.append('version', bridgeReleaseForm.version);
      formData.append('build', bridgeReleaseForm.build);
      formData.append('releasedAt', new Date(bridgeReleaseForm.releasedAt).toISOString());
      formData.append('minAndroid', bridgeReleaseForm.minAndroid);

      await api.post('/admin/bridge-app/android/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setBridgeReleaseForm({
        version: '',
        build: '1',
        releasedAt: '',
        minAndroid: '8.0',
        file: null,
      });
      setFeedback('Release do Bridge App Android enviada com sucesso.');
      await loadData();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'Nao foi possivel enviar a release do Bridge App.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">{pageCopy.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{pageCopy.description}</p>
      </div>

      {feedback && <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">{feedback}</div>}
      {error && <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>}

      {section === 'bridge' && (
      <div className="card space-y-4 p-5">
        <div className="flex items-center gap-2">
          <UploadCloud className="h-5 w-5 text-primary" />
          <div>
            <h2 className="text-lg font-bold text-white">Bridge App Android</h2>
            <p className="text-sm text-muted-foreground">Publicacao do APK no Cloudflare R2 e controle da release ativa para o painel dos restaurantes.</p>
          </div>
        </div>

        <form onSubmit={handleBridgeReleaseUpload} className="grid gap-3 xl:grid-cols-[1.2fr_0.8fr]">
          <div className="grid gap-3 md:grid-cols-2">
            <input
              className="input"
              placeholder="Versao ex.: 1.0.0"
              value={bridgeReleaseForm.version}
              onChange={(e) => setBridgeReleaseForm((current) => ({ ...current, version: e.target.value }))}
              required
            />
            <input
              className="input"
              type="number"
              min="1"
              placeholder="Build"
              value={bridgeReleaseForm.build}
              onChange={(e) => setBridgeReleaseForm((current) => ({ ...current, build: e.target.value }))}
              required
            />
            <input
              className="input"
              type="datetime-local"
              value={bridgeReleaseForm.releasedAt}
              onChange={(e) => setBridgeReleaseForm((current) => ({ ...current, releasedAt: e.target.value }))}
              required
            />
            <input
              className="input"
              placeholder="Android minimo"
              value={bridgeReleaseForm.minAndroid}
              onChange={(e) => setBridgeReleaseForm((current) => ({ ...current, minAndroid: e.target.value }))}
              required
            />
            <input
              className="input md:col-span-2"
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={(e) => setBridgeReleaseForm((current) => ({ ...current, file: e.target.files?.[0] ?? null }))}
              required
            />
          </div>

          <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4 text-sm text-muted-foreground">
            <p className="font-semibold text-white">Fluxo da release</p>
            <p className="mt-2">1. Envie o APK assinado ou debug.</p>
            <p className="mt-1">2. O backend sobe o arquivo para o bucket do Cloudflare R2.</p>
            <p className="mt-1">3. A nova release vira ativa no painel dos restaurantes.</p>
            <button type="submit" className="btn-primary mt-4 w-full px-4 py-2 text-sm" disabled={isSubmitting}>
              {isSubmitting ? 'Enviando release...' : 'Publicar release Android'}
            </button>
          </div>
        </form>

        <div className="grid gap-3">
          {bridgeReleases.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#2A2A2A] p-5 text-sm text-muted-foreground">
              Nenhuma release do Bridge App Android publicada ainda.
            </div>
          ) : (
            bridgeReleases.slice(0, 5).map((release) => (
              <div key={release.id} className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-white">{release.version} ({release.build})</p>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${release.isActive ? 'bg-emerald-500/15 text-emerald-300' : 'bg-[#1B1B1B] text-muted-foreground'}`}>
                        {release.isActive ? 'Ativa' : 'Historico'}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Android minimo {release.minAndroid} · {formatFileSize(release.fileSize)} · {new Date(release.releasedAt).toLocaleString('pt-BR')}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">{release.originalFileName}</p>
                  </div>
                  {release.downloadUrl ? (
                    <a
                      href={release.downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-xl border border-[#2A2A2A] px-3 py-2 text-sm text-white"
                    >
                      <Download className="h-4 w-4" />
                      Baixar release
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-2 rounded-xl border border-[#2A2A2A] px-3 py-2 text-sm text-muted-foreground">
                      <Download className="h-4 w-4" />
                      Sem URL publica
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      )}

      {section === 'overview' && (
      <div className="grid gap-4 md:grid-cols-3">
        {metricCards.map((card) => (
          <div key={card.label} className="card flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15">
              <card.icon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{card.label}</p>
              <p className="text-2xl font-black text-white">{card.value}</p>
            </div>
          </div>
        ))}
      </div>
      )}

      {(section === 'overview' || section === 'stability') && (
      <div className="card space-y-5 p-5">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-bold text-white">Estabilidade tecnica</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Monitoramento proativo de risco operacional, pagamentos, impressao, WhatsApp e ativos 3D por restaurante.
            </p>
          </div>
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] px-4 py-3 text-sm text-muted-foreground">
            Ultima leitura:{' '}
            <span className="font-semibold text-white">
              {technicalHealth?.generatedAt ? new Date(technicalHealth.generatedAt).toLocaleString('pt-BR') : 'aguardando dados'}
            </span>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {stabilityCards.map((card) => {
            const toneClass =
              card.tone === 'critical'
                ? 'bg-red-500/10 text-red-300'
                : card.tone === 'warning'
                  ? 'bg-amber-500/10 text-amber-300'
                  : 'bg-emerald-500/10 text-emerald-300';

            return (
              <div key={card.label} className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm text-muted-foreground">{card.label}</p>
                    <p className="mt-2 text-3xl font-black text-white">{card.value}</p>
                  </div>
                  <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${toneClass}`}>
                    <card.icon className="h-5 w-5" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {restaurantsWithIssues.length === 0 ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-5">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-300" />
              <div>
                <p className="font-semibold text-emerald-200">Nenhum risco operacional relevante detectado.</p>
                <p className="text-sm text-emerald-300/80">Todos os restaurantes monitorados estao sem alertas criticos neste momento.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {restaurantsWithIssues.slice(0, 8).map((restaurant) => (
              <div key={restaurant.id} className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-base font-semibold text-white">{restaurant.name}</p>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        restaurant.healthScore >= 85
                          ? 'bg-emerald-500/10 text-emerald-300'
                          : restaurant.healthScore >= 60
                            ? 'bg-amber-500/10 text-amber-300'
                            : 'bg-red-500/10 text-red-300'
                      }`}>
                        Score {restaurant.healthScore}
                      </span>
                      {!restaurant.active && (
                        <span className="rounded-full bg-[#1B1B1B] px-2 py-0.5 text-xs font-semibold text-muted-foreground">
                          Desativado
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      /{restaurant.slug} {restaurant.owner ? `· dono: ${restaurant.owner.name}` : '· sem dono principal'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-red-500/10 px-2 py-1 font-semibold text-red-300">
                      {restaurant.issueCounts.critical} criticos
                    </span>
                    <span className="rounded-full bg-amber-500/10 px-2 py-1 font-semibold text-amber-300">
                      {restaurant.issueCounts.warning} alertas
                    </span>
                    <span className="rounded-full bg-sky-500/10 px-2 py-1 font-semibold text-sky-300">
                      {restaurant.issueCounts.info} infos
                    </span>
                  </div>
                </div>

                <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  <div className="rounded-xl border border-[#262626] bg-[#101010] px-3 py-2 text-xs text-muted-foreground">
                    Pagamento: <span className={restaurant.signals.paymentConfigured ? 'font-semibold text-emerald-300' : 'font-semibold text-red-300'}>{restaurant.signals.paymentConfigured ? 'configurado' : 'pendente'}</span>
                  </div>
                  <div className="rounded-xl border border-[#262626] bg-[#101010] px-3 py-2 text-xs text-muted-foreground">
                    WhatsApp: <span className={restaurant.signals.whatsappStatus === 'CONNECTED' ? 'font-semibold text-emerald-300' : 'font-semibold text-amber-300'}>{restaurant.signals.whatsappStatus}</span>
                  </div>
                  <div className="rounded-xl border border-[#262626] bg-[#101010] px-3 py-2 text-xs text-muted-foreground">
                    Impressoras: <span className="font-semibold text-white">{restaurant.signals.activePrinterConfigs}</span>
                  </div>
                  <div className="rounded-xl border border-[#262626] bg-[#101010] px-3 py-2 text-xs text-muted-foreground">
                    Bridges online: <span className="font-semibold text-white">{restaurant.signals.onlineBridgeDevices}/{restaurant.signals.activeBridgeDevices}</span>
                  </div>
                  <div className="rounded-xl border border-[#262626] bg-[#101010] px-3 py-2 text-xs text-muted-foreground">
                    Pix em risco: <span className={`font-semibold ${restaurant.signals.atRiskMercadoPagoOrders > 0 ? 'text-amber-300' : 'text-white'}`}>{restaurant.signals.atRiskMercadoPagoOrders}</span>
                  </div>
                  <div className="rounded-xl border border-[#262626] bg-[#101010] px-3 py-2 text-xs text-muted-foreground">
                    Falhas de impressao: <span className={`font-semibold ${restaurant.signals.failedPrintJobs > 0 ? 'text-red-300' : 'text-white'}`}>{restaurant.signals.failedPrintJobs}</span>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  {restaurant.issues.slice(0, 4).map((issue) => (
                    <div
                      key={`${restaurant.id}-${issue.code}`}
                      className={`rounded-xl border px-3 py-2 text-sm ${
                        issue.severity === 'critical'
                          ? 'border-red-500/30 bg-red-500/10 text-red-200'
                          : issue.severity === 'warning'
                            ? 'border-amber-500/30 bg-amber-500/10 text-amber-200'
                            : 'border-sky-500/30 bg-sky-500/10 text-sky-200'
                      }`}
                    >
                      {issue.message}
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setFocusedHealthRestaurantId(restaurant.id)}
                    className="rounded-xl border border-primary/30 bg-primary/10 px-3 py-2 text-sm font-semibold text-white transition hover:border-primary/50 hover:bg-primary/15"
                  >
                    Centralizar diagnostico
                  </button>
                  <a
                    href={`/store/${restaurant.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-xl border border-[#2A2A2A] px-3 py-2 text-sm font-semibold text-white transition hover:border-primary/40 hover:text-primary"
                  >
                    Abrir loja
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {focusedHealthRestaurant && (
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#111] p-4">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary">Acao rapida</p>
                <h3 className="mt-1 text-lg font-bold text-white">{focusedHealthRestaurant.name}</h3>
                <p className="text-sm text-muted-foreground">
                  Diagnostico rapido para atacar risco financeiro e operacional sem sair do painel da plataforma.
                </p>
              </div>
              <a
                href={`/store/${focusedHealthRestaurant.slug}`}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border border-[#2A2A2A] px-3 py-2 text-sm font-semibold text-white transition hover:border-primary/40 hover:text-primary"
              >
                Abrir loja publica
              </a>
            </div>

            <div className="mt-4 grid gap-4 xl:grid-cols-2">
              <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-white">Pedidos com risco de pagamento</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Mercado Pago aguardando ha muito tempo ou pago sem conciliacao final.
                    </p>
                  </div>
                  <span className="rounded-full bg-amber-500/10 px-2 py-1 text-xs font-semibold text-amber-300">
                    {focusedHealthRestaurant.signals.atRiskMercadoPagoOrders + focusedHealthRestaurant.signals.paidAwaitingOrders} alerta(s)
                  </span>
                </div>

                <div className="mt-4 space-y-2">
                  {focusedHealthRestaurant.samples.atRiskOrders.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-[#2A2A2A] px-3 py-4 text-sm text-muted-foreground">
                      Nenhum pedido de risco encontrado para este restaurante.
                    </div>
                  ) : (
                    focusedHealthRestaurant.samples.atRiskOrders.map((order) => (
                      <div key={order.id} className="rounded-xl border border-[#2A2A2A] bg-[#101010] px-3 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-white">{order.shortId}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(order.createdAt).toLocaleString('pt-BR')} · {order.status} · {order.paymentStatus ?? 'sem status'}
                            </p>
                          </div>
                          <p className="text-sm font-semibold text-white">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(order.total)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-white">Falhas recentes de impressao</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Jobs com erro nas ultimas 24h para acelerar o suporte de operacao.
                    </p>
                  </div>
                  <span className="rounded-full bg-red-500/10 px-2 py-1 text-xs font-semibold text-red-300">
                    {focusedHealthRestaurant.signals.failedPrintJobs} falha(s)
                  </span>
                </div>

                <div className="mt-4 space-y-2">
                  {focusedHealthRestaurant.samples.printFailures.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-[#2A2A2A] px-3 py-4 text-sm text-muted-foreground">
                      Nenhuma falha recente de impressao para este restaurante.
                    </div>
                  ) : (
                    focusedHealthRestaurant.samples.printFailures.map((job) => (
                      <div key={job.id} className="rounded-xl border border-[#2A2A2A] bg-[#101010] px-3 py-3">
                        <p className="text-sm font-semibold text-white">{job.printerName}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(job.createdAt).toLocaleString('pt-BR')}
                          {job.orderShortId ? ` · pedido ${job.orderShortId}` : ''}
                        </p>
                        <p className="mt-2 text-sm text-red-200">{job.errorMessage}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {section === 'restaurants' && (
      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        <form onSubmit={handleCreateRestaurant} className="card space-y-4 p-5">
          <div>
            <h2 className="text-lg font-bold text-white">Novo restaurante</h2>
            <p className="mt-1 text-sm text-muted-foreground">Cria o restaurante e o dono inicial em uma unica operacao.</p>
          </div>

          <input className="input" placeholder="Nome do restaurante" value={createForm.name} onChange={(e) => handleCreateChange('name', e.target.value)} required />
          <input className="input" placeholder="Slug publico" value={createForm.slug} onChange={(e) => handleCreateChange('slug', e.target.value)} required />
          <input className="input" placeholder="Endereco" value={createForm.address} onChange={(e) => handleCreateChange('address', e.target.value)} />
          <input className="input" placeholder="Telefone" value={createForm.phone} onChange={(e) => handleCreateChange('phone', e.target.value)} />
          <input className="input" type="number" min="0" placeholder="Dias de trial" value={createForm.trialDays} onChange={(e) => handleCreateChange('trialDays', e.target.value)} />

          <div className="rounded-2xl border border-[#2A2A2A] bg-[#141414] p-4">
            <p className="text-sm font-semibold text-white">Recursos operacionais</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Escolha se esse restaurante vai operar com salao, mesas e agenda de reservas. Para operacoes so delivery, desative os dois recursos.
            </p>
            <div className="mt-4 grid gap-3">
              {operationalPresets.map((preset) => {
                const isSelected = createSelectedPreset === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyCreatePreset(preset.id)}
                    className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                      isSelected
                        ? 'border-primary/50 bg-primary/10 text-white'
                        : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                    }`}
                  >
                    <p className="text-sm font-semibold">{preset.label}</p>
                    <p className="mt-1 text-xs">{preset.description}</p>
                  </button>
                );
              })}
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => handleCreateToggle('hasTableModule', !createForm.hasTableModule)}
                className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                  createForm.hasTableModule
                    ? 'border-primary/40 bg-primary/10 text-white'
                    : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                }`}
              >
                <p className="text-sm font-semibold">Mesas</p>
                <p className="mt-1 text-xs">Libera cadastro, ocupacao e configuracao do salao.</p>
              </button>
              <button
                type="button"
                onClick={() => handleCreateToggle('hasReservationModule', !createForm.hasReservationModule)}
                className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                  createForm.hasReservationModule
                    ? 'border-primary/40 bg-primary/10 text-white'
                    : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                }`}
              >
                <p className="text-sm font-semibold">Reservas</p>
                <p className="mt-1 text-xs">Libera agenda, confirmacao de reserva e pagina publica de reserva.</p>
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-[#2A2A2A] bg-[#141414] p-4">
            <p className="text-sm font-semibold text-white">Canais e modulos iniciais</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Defina o baseline do tenant ja na criacao para evitar configuracao manual logo apos o onboarding.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => handleCreateToggle('deliveryEnabled', !createForm.deliveryEnabled)}
                className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                  createForm.deliveryEnabled
                    ? 'border-primary/40 bg-primary/10 text-white'
                    : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                }`}
              >
                <p className="text-sm font-semibold">Delivery {createForm.deliveryEnabled ? 'ativo' : 'desligado'}</p>
                <p className="mt-1 text-xs">Controla se o cardapio aceita pedidos para entrega.</p>
              </button>
              <button
                type="button"
                onClick={() => handleCreateToggle('pickupEnabled', !createForm.pickupEnabled)}
                className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                  createForm.pickupEnabled
                    ? 'border-primary/40 bg-primary/10 text-white'
                    : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                }`}
              >
                <p className="text-sm font-semibold">Retirada {createForm.pickupEnabled ? 'ativa' : 'desligada'}</p>
                <p className="mt-1 text-xs">Libera pedidos para retirada sem entrega.</p>
              </button>
              <button
                type="button"
                onClick={() => handleCreateToggle('printingModuleEnabled', !createForm.printingModuleEnabled)}
                className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                  createForm.printingModuleEnabled
                    ? 'border-primary/40 bg-primary/10 text-white'
                    : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                }`}
              >
                <p className="text-sm font-semibold">Impressao {createForm.printingModuleEnabled ? 'liberada' : 'desabilitada'}</p>
                <p className="mt-1 text-xs">Mantem o modulo de impressao ativo desde o primeiro acesso.</p>
              </button>
              <button
                type="button"
                onClick={() => handleCreateToggle('hasQrMenu', !createForm.hasQrMenu)}
                className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                  createForm.hasQrMenu
                    ? 'border-primary/40 bg-primary/10 text-white'
                    : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                }`}
              >
                <p className="text-sm font-semibold">QR menu {createForm.hasQrMenu ? 'liberado' : 'desabilitado'}</p>
                <p className="mt-1 text-xs">Define se o restaurante nasce pronto para menu via QR code.</p>
              </button>
              <button
                type="button"
                onClick={() => handleCreateToggle('has3dModelModule', !createForm.has3dModelModule)}
                className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                  createForm.has3dModelModule
                    ? 'border-primary/40 bg-primary/10 text-white'
                    : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                }`}
              >
                <p className="text-sm font-semibold">3D / AR {createForm.has3dModelModule ? 'liberado' : 'desabilitado'}</p>
                <p className="mt-1 text-xs">Ativa upload e exibicao de modelos 3D no tenant desde a criacao.</p>
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-[#2A2A2A] bg-[#141414] p-4">
            <p className="text-sm font-semibold text-white">Parametros comerciais iniciais</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Esses campos preenchem o estado inicial de configuracao do restaurante, mas continuam editaveis depois no admin.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <select className="input appearance-none bg-[#1A1A1A]" value={createForm.currency} onChange={(e) => handleCreateChange('currency', e.target.value)}>
                <option value="BRL">BRL</option>
                <option value="USD">USD</option>
              </select>
              <select className="input appearance-none bg-[#1A1A1A]" value={createForm.theme} onChange={(e) => handleCreateChange('theme', e.target.value)}>
                <option value="dark">Dark</option>
                <option value="light">Light</option>
              </select>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                placeholder="Taxa fixa de delivery"
                value={createForm.deliveryFixedFee}
                onChange={(e) => handleCreateChange('deliveryFixedFee', e.target.value)}
              />
            </div>
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pagamentos aceitos no delivery</p>
              <div className="mt-3 grid gap-3">
                {deliveryPaymentMethodOptions.map((option) => {
                  const isSelected = createForm.deliveryPaymentMethods.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleCreatePaymentMethodToggle(option.value)}
                      className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                        isSelected
                          ? 'border-primary/40 bg-primary/10 text-white'
                          : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                      }`}
                    >
                      <p className="text-sm font-semibold">{option.label}</p>
                      <p className="mt-1 text-xs">{option.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="border-t border-[#2A2A2A] pt-4">
            <p className="mb-3 text-sm font-semibold text-white">Dono inicial</p>
            <div className="space-y-3">
              <input className="input" placeholder="Nome do dono" value={createForm.ownerName} onChange={(e) => handleCreateChange('ownerName', e.target.value)} required />
              <input className="input" type="email" placeholder="Email do dono" value={createForm.ownerEmail} onChange={(e) => handleCreateChange('ownerEmail', e.target.value)} required />
              <input className="input" type="password" placeholder="Senha inicial" value={createForm.ownerPassword} onChange={(e) => handleCreateChange('ownerPassword', e.target.value)} required />
            </div>
          </div>

          <button type="submit" disabled={isSubmitting} className="btn-primary w-full py-3 disabled:opacity-50">
            {isSubmitting ? 'Criando...' : 'Criar restaurante'}
          </button>
        </form>

        <div className="card p-5">
          <div className="mb-4">
            <h2 className="text-lg font-bold text-white">Restaurantes cadastrados</h2>
            <p className="mt-1 text-sm text-muted-foreground">Edite dados, altere status e redefina o acesso do dono.</p>
          </div>

          <div className="space-y-4">
            {restaurants.map((restaurant) => (
              <div key={restaurant.id} className="rounded-2xl border border-[#2A2A2A] bg-[#111] p-4">
                {editingId === restaurant.id ? (
                  <div className="space-y-3">
                    <input className="input" value={editForm.name} onChange={(e) => setEditForm((current) => ({ ...current, name: e.target.value }))} />
                    <input className="input" value={editForm.slug} onChange={(e) => setEditForm((current) => ({ ...current, slug: e.target.value }))} />
                    <input className="input" value={editForm.address} onChange={(e) => setEditForm((current) => ({ ...current, address: e.target.value }))} />
                    <input className="input" value={editForm.phone} onChange={(e) => setEditForm((current) => ({ ...current, phone: e.target.value }))} />
                    <div className="rounded-2xl border border-[#2A2A2A] bg-[#141414] p-4">
                      <p className="text-sm font-semibold text-white">Preset operacional</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Use um preset rapido para alinhar este tenant ao tipo real de operacao do restaurante.
                      </p>
                      <div className="mt-4 grid gap-3">
                        {operationalPresets.map((preset) => {
                          const isSelected = editSelectedPreset === preset.id;
                          return (
                            <button
                              key={preset.id}
                              type="button"
                              onClick={() => applyEditPreset(preset.id)}
                              className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                                isSelected
                                  ? 'border-primary/50 bg-primary/10 text-white'
                                  : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                              }`}
                            >
                              <p className="text-sm font-semibold">{preset.label}</p>
                              <p className="mt-1 text-xs">{preset.description}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => setEditForm((current) => ({ ...current, hasTableModule: !current.hasTableModule }))}
                        className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                          editForm.hasTableModule
                            ? 'border-primary/40 bg-primary/10 text-white'
                            : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                        }`}
                      >
                        <p className="text-sm font-semibold">Mesas {editForm.hasTableModule ? 'liberadas' : 'desabilitadas'}</p>
                        <p className="mt-1 text-xs">Controla se o tenant pode operar com mesas.</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditForm((current) => ({ ...current, hasReservationModule: !current.hasReservationModule }))}
                        className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                          editForm.hasReservationModule
                            ? 'border-primary/40 bg-primary/10 text-white'
                            : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                        }`}
                      >
                        <p className="text-sm font-semibold">Reservas {editForm.hasReservationModule ? 'liberadas' : 'desabilitadas'}</p>
                        <p className="mt-1 text-xs">Controla se o tenant pode acessar agenda e reservas.</p>
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditForm((current) => ({ ...current, has3dModelModule: !current.has3dModelModule }))}
                      className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                        editForm.has3dModelModule
                          ? 'border-primary/40 bg-primary/10 text-white'
                          : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                      }`}
                    >
                      <p className="text-sm font-semibold">Modelos 3D / AR {editForm.has3dModelModule ? 'liberados' : 'desabilitados'}</p>
                      <p className="mt-1 text-xs">Feature flag da plataforma para upload e exibicao de arquivos .glb no tenant.</p>
                    </button>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => setEditForm((current) => ({ ...current, deliveryEnabled: !current.deliveryEnabled }))}
                        className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                          editForm.deliveryEnabled
                            ? 'border-primary/40 bg-primary/10 text-white'
                            : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                        }`}
                      >
                        <p className="text-sm font-semibold">Delivery {editForm.deliveryEnabled ? 'ativo' : 'desligado'}</p>
                        <p className="mt-1 text-xs">Controla pedidos com entrega no tenant.</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditForm((current) => ({ ...current, pickupEnabled: !current.pickupEnabled }))}
                        className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                          editForm.pickupEnabled
                            ? 'border-primary/40 bg-primary/10 text-white'
                            : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                        }`}
                      >
                        <p className="text-sm font-semibold">Retirada {editForm.pickupEnabled ? 'ativa' : 'desligada'}</p>
                        <p className="mt-1 text-xs">Libera pedidos para retirada no local.</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditForm((current) => ({ ...current, printingModuleEnabled: !current.printingModuleEnabled }))}
                        className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                          editForm.printingModuleEnabled
                            ? 'border-primary/40 bg-primary/10 text-white'
                            : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                        }`}
                      >
                        <p className="text-sm font-semibold">Impressao {editForm.printingModuleEnabled ? 'liberada' : 'desabilitada'}</p>
                        <p className="mt-1 text-xs">Mantem o modulo de impressao ativo para a operacao.</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditForm((current) => ({ ...current, hasQrMenu: !current.hasQrMenu }))}
                        className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                          editForm.hasQrMenu
                            ? 'border-primary/40 bg-primary/10 text-white'
                            : 'border-[#2A2A2A] bg-[#101010] text-muted-foreground'
                        }`}
                      >
                        <p className="text-sm font-semibold">QR menu {editForm.hasQrMenu ? 'liberado' : 'desabilitado'}</p>
                        <p className="mt-1 text-xs">Permite cardapio por QR code desde a configuracao do tenant.</p>
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => saveRestaurant(restaurant.id)} disabled={savingRestaurantId === restaurant.id} className="btn-primary px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60">{savingRestaurantId === restaurant.id ? 'Salvando...' : 'Salvar'}</button>
                      <button type="button" onClick={() => setEditingId(null)} disabled={savingRestaurantId === restaurant.id} className="rounded-xl border border-[#2A2A2A] px-4 py-2 text-sm text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60">Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-bold text-white">{restaurant.name}</h3>
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${restaurant.active ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'}`}>
                            {restaurant.active ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">/{restaurant.slug}</p>
                        {restaurant.address && <p className="mt-2 text-sm text-muted-foreground">{restaurant.address}</p>}
                        {restaurant.phone && <p className="mt-1 text-sm text-muted-foreground">{restaurant.phone}</p>}
                      </div>

                      <div className="flex gap-2">
                        <button type="button" onClick={() => startEditing(restaurant)} className="rounded-xl border border-[#2A2A2A] p-2 text-muted-foreground hover:text-white">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => toggleRestaurantStatus(restaurant)} disabled={togglingRestaurantId === restaurant.id} className="rounded-xl border border-[#2A2A2A] p-2 text-muted-foreground hover:text-white disabled:cursor-not-allowed disabled:opacity-60">
                          {togglingRestaurantId === restaurant.id ? <span className="px-1 text-[11px] font-semibold">...</span> : <Power className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-2 text-sm text-muted-foreground sm:grid-cols-4">
                      <div className="rounded-xl bg-[#151515] px-3 py-2">Usuarios: <span className="text-white">{restaurant._count.users}</span></div>
                      <div className="rounded-xl bg-[#151515] px-3 py-2">Pedidos: <span className="text-white">{restaurant._count.orders}</span></div>
                      <div className="rounded-xl bg-[#151515] px-3 py-2">Categorias: <span className="text-white">{restaurant._count.categories}</span></div>
                      <div className="rounded-xl bg-[#151515] px-3 py-2">Produtos: <span className="text-white">{restaurant._count.products}</span></div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <span className={`rounded-full px-2.5 py-1 font-semibold ${restaurant.settings?.hasReservationModule !== false ? 'bg-primary/15 text-primary' : 'bg-[#1B1B1B] text-muted-foreground'}`}>
                        Reservas {restaurant.settings?.hasReservationModule !== false ? 'liberadas' : 'desabilitadas'}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 font-semibold ${restaurant.settings?.hasTableModule !== false ? 'bg-primary/15 text-primary' : 'bg-[#1B1B1B] text-muted-foreground'}`}>
                        Mesas {restaurant.settings?.hasTableModule !== false ? 'liberadas' : 'desabilitadas'}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 font-semibold ${restaurant.settings?.has3dModelModule === true ? 'bg-primary/15 text-primary' : 'bg-[#1B1B1B] text-muted-foreground'}`}>
                        3D / AR {restaurant.settings?.has3dModelModule === true ? 'liberado' : 'desabilitado'}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 font-semibold ${restaurant.settings?.hasQrMenu !== false ? 'bg-primary/15 text-primary' : 'bg-[#1B1B1B] text-muted-foreground'}`}>
                        QR menu {restaurant.settings?.hasQrMenu !== false ? 'liberado' : 'desabilitado'}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 font-semibold ${restaurant.settings?.printingModuleEnabled !== false ? 'bg-primary/15 text-primary' : 'bg-[#1B1B1B] text-muted-foreground'}`}>
                        Impressao {restaurant.settings?.printingModuleEnabled !== false ? 'ativa' : 'desligada'}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 font-semibold ${restaurant.settings?.deliveryEnabled !== false ? 'bg-primary/15 text-primary' : 'bg-[#1B1B1B] text-muted-foreground'}`}>
                        Delivery {restaurant.settings?.deliveryEnabled !== false ? 'ativo' : 'desligado'}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 font-semibold ${restaurant.settings?.pickupEnabled !== false ? 'bg-primary/15 text-primary' : 'bg-[#1B1B1B] text-muted-foreground'}`}>
                        Retirada {restaurant.settings?.pickupEnabled !== false ? 'ativa' : 'desligada'}
                      </span>
                    </div>

                    <div className="mt-4 rounded-xl border border-[#2A2A2A] bg-[#151515] p-3">
                      <p className="text-sm font-semibold text-white">Dono</p>
                      <p className="mt-1 text-sm text-muted-foreground">{restaurant.owner?.name ?? 'Nao definido'}</p>
                      <p className="text-sm text-muted-foreground">{restaurant.owner?.email ?? 'Sem e-mail'}</p>
                      {restaurant.owner && (
                        <div className="mt-3 flex flex-col gap-2 md:flex-row">
                          <input
                            className="input md:flex-1"
                            type="password"
                            placeholder="Nova senha do dono"
                            value={passwordDrafts[restaurant.owner.id] ?? ''}
                            onChange={(e) => setPasswordDrafts((current) => ({ ...current, [restaurant.owner!.id]: e.target.value }))}
                          />
                          <button type="button" onClick={() => resetOwnerPassword(restaurant.owner!.id)} disabled={resettingOwnerId === restaurant.owner!.id} className="btn-primary flex items-center justify-center gap-2 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60">
                            <KeyRound className="h-4 w-4" />
                            {resettingOwnerId === restaurant.owner!.id ? 'Resetando...' : 'Resetar senha'}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 rounded-xl border border-[#2A2A2A] bg-[#151515] p-3">
                      <div className="mb-3">
                        <p className="text-sm font-semibold text-white">Assinatura</p>
                        <p className="text-xs text-muted-foreground">Trial manual. Proxima cobranca calculada automaticamente quando a assinatura ficar ativa.</p>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <select
                          className="input appearance-none bg-[#1A1A1A]"
                          value={subscriptionDrafts[restaurant.id]?.subscriptionPlan ?? restaurant.subscriptionPlan}
                          onChange={(e) =>
                            setSubscriptionDrafts((current) => ({
                              ...current,
                              [restaurant.id]: {
                                ...current[restaurant.id],
                                subscriptionPlan: e.target.value as PlatformRestaurant['subscriptionPlan'],
                              },
                            }))
                          }
                        >
                          <option value="BASIC">Basic</option>
                          <option value="PRO">Pro</option>
                          <option value="ENTERPRISE">Enterprise</option>
                        </select>
                        <select
                          className="input appearance-none bg-[#1A1A1A]"
                          value={subscriptionDrafts[restaurant.id]?.subscriptionStatus ?? restaurant.subscriptionStatus}
                          onChange={(e) =>
                            setSubscriptionDrafts((current) => ({
                              ...current,
                              [restaurant.id]: {
                                ...current[restaurant.id],
                                subscriptionStatus: e.target.value as PlatformRestaurant['subscriptionStatus'],
                              },
                            }))
                          }
                        >
                          <option value="TRIALING">Trial</option>
                          <option value="ACTIVE">Active</option>
                          <option value="PAST_DUE">Past Due</option>
                          <option value="CANCELED">Canceled</option>
                        </select>
                        <select
                          className="input appearance-none bg-[#1A1A1A]"
                          value={subscriptionDrafts[restaurant.id]?.billingCycle ?? restaurant.billingCycle}
                          onChange={(e) =>
                            setSubscriptionDrafts((current) => ({
                              ...current,
                              [restaurant.id]: {
                                ...current[restaurant.id],
                                billingCycle: e.target.value as PlatformRestaurant['billingCycle'],
                              },
                            }))
                          }
                        >
                          <option value="MONTHLY">Monthly</option>
                          <option value="YEARLY">Yearly</option>
                        </select>
                        <input
                          className="input"
                          type="number"
                          step="0.01"
                          value={subscriptionDrafts[restaurant.id]?.subscriptionPrice ?? String(restaurant.subscriptionPrice)}
                          onChange={(e) =>
                            setSubscriptionDrafts((current) => ({
                              ...current,
                              [restaurant.id]: {
                                ...current[restaurant.id],
                                subscriptionPrice: e.target.value,
                              },
                            }))
                          }
                          placeholder="Valor"
                        />
                        <input
                          className="input"
                          type="number"
                          min="0"
                          value={subscriptionDrafts[restaurant.id]?.trialDays ?? ''}
                          onChange={(e) =>
                            setSubscriptionDrafts((current) => ({
                              ...current,
                              [restaurant.id]: {
                                ...current[restaurant.id],
                                trialDays: e.target.value,
                              },
                            }))
                          }
                          placeholder="Dias de trial"
                        />
                        <div className="rounded-xl border border-[#2A2A2A] bg-[#1A1A1A] px-3 py-2">
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Proxima cobranca</p>
                          <p className="mt-1 text-sm font-semibold text-white">
                            {restaurant.nextBillingAt
                              ? new Date(restaurant.nextBillingAt).toLocaleDateString('pt-BR')
                              : 'Automatica apos o pagamento'}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <button type="button" onClick={() => saveSubscription(restaurant.id)} disabled={savingSubscriptionRestaurantId === restaurant.id} className="btn-primary px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60">
                          {savingSubscriptionRestaurantId === restaurant.id ? 'Salvando...' : 'Salvar assinatura'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {section === 'users' && (
      <div className="card p-5">
        <div className="mb-4">
          <h2 className="text-lg font-bold text-white">Usuarios da plataforma</h2>
          <p className="mt-1 text-sm text-muted-foreground">Visao global dos acessos cadastrados.</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-[#2A2A2A] text-left text-muted-foreground">
                <th className="px-3 py-3 font-medium">Nome</th>
                <th className="px-3 py-3 font-medium">Email</th>
                <th className="px-3 py-3 font-medium">Role</th>
                <th className="px-3 py-3 font-medium">Restaurante</th>
                <th className="px-3 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b border-[#2A2A2A] last:border-0">
                  <td className="px-3 py-3 text-white">{user.name}</td>
                  <td className="px-3 py-3 text-muted-foreground">{user.email}</td>
                  <td className="px-3 py-3 text-muted-foreground">{user.role}</td>
                  <td className="px-3 py-3 text-muted-foreground">
                    {user.establishment ? `${user.establishment.name} (${user.establishment.slug})` : 'Plataforma'}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${!user.establishment || user.establishment.active ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'}`}>
                      {!user.establishment || user.establishment.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
};

export default PlatformDashboard;

function getRemainingTrialDays(trialEndsAt?: string | null) {
  if (!trialEndsAt) return '';

  const diff = new Date(trialEndsAt).getTime() - Date.now();
  if (diff <= 0) return '0';

  return String(Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function formatFileSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const power = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / (1024 ** power)).toFixed(power === 0 ? 0 : 1)} ${units[power]}`;
}
